import React from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectClubName, selectSettings } from "../store/slices/settingsSlice";
import { useTheme } from "../context/ThemeContext";
import { getPageBackground, getCardBackground, getPrimaryText, getMutedText } from "../utils/themeUtils";
import SEOHead from "../components/seo/SEOHead";
import OrganizationSchema from "../components/seo/OrganizationSchema";
import LocalBusinessSchema from "../components/seo/LocalBusinessSchema";

const LandingPage: React.FC = () => {
  const location = useLocation();
  // THEME
  const { isDarkMode } = useTheme();
  // HELPER
  const settings: any = useSelector(selectSettings);
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;
  const clubName = useSelector(selectClubName);

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title={clubName || "Home"}
        description={settings?.seo_default_description || `Welcome to ${clubName || "our club"}. Join our community of athletes and coaches.`}
      />
      <OrganizationSchema />
      <LocalBusinessSchema />
      <Navbar transparent={true} />

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${settings?.hero_image_url || "/assets/hero-bg.jpg"}')`,
            opacity: 0.4,
          }}
        ></div>
        <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16 relative z-10">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              {clubName || "Athletics Club"}
            </h1>
            <p className="text-lg sm:text-xl mb-6 sm:mb-8 leading-relaxed">
              Join our community of athletes and coaches. Experience top-notch
              training programs, professional coaching, and a supportive
              environment to help you reach your full potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-4">
              <Link
                to={`/${tenantId}/programs`}
                className="bg-[var(--cc-action)] text-[var(--cc-action-text)] px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-[var(--cc-action-hover)] text-center transition-colors duration-200 text-base sm:text-lg"
              >
                Explore Programs
              </Link>
              <Link
                to={`/${tenantId}/register`}
                className="bg-transparent border-2 border-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 text-center transition-all duration-200 text-base sm:text-lg"
              >
                Join Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-12 sm:py-16 ${getPageBackground(isDarkMode)}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${getPrimaryText(isDarkMode)}`}>
              Why Choose Us
            </h2>
            <p className={`${getMutedText(isDarkMode)} mt-3 sm:mt-4 max-w-2xl mx-auto text-base sm:text-lg px-4 sm:px-0`}>
              We offer comprehensive programs designed to develop athletes of
              all ages and skill levels.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className={`${getCardBackground(isDarkMode)} p-6 sm:p-8 rounded-lg shadow-sm`}>
              <div className={`${getMutedText(isDarkMode)} mb-4`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className={`text-lg sm:text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-2`}>
                Expert Coaching
              </h3>
              <p className={`text-sm sm:text-base ${getMutedText(isDarkMode)}`}>
                Our experienced coaches bring years of experience and passion to
                every session, ensuring athletes receive the best guidance and
                support.
              </p>
            </div>

            <div className={`${getCardBackground(isDarkMode)} p-6 sm:p-8 rounded-lg shadow-sm`}>
              <div className={`${getMutedText(isDarkMode)} mb-4`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className={`text-lg sm:text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-2`}>
                Supportive Community
              </h3>
              <p className={`text-sm sm:text-base ${getMutedText(isDarkMode)}`}>
                Join a community that celebrates achievements, provides
                encouragement, and fosters lifelong friendships among athletes
                and families.
              </p>
            </div>

            <div className={`${getCardBackground(isDarkMode)} p-6 sm:p-8 rounded-lg shadow-sm`}>
              <div className={`${getMutedText(isDarkMode)} mb-4`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 className={`text-lg sm:text-xl font-semibold ${getPrimaryText(isDarkMode)} mb-2`}>
                Modern Facilities
              </h3>
              <p className={`text-sm sm:text-base ${getMutedText(isDarkMode)}`}>
                Train in state-of-the-art facilities equipped with the latest
                equipment and technology to enhance performance and safety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Preview Section 
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800">Our Programs</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Discover our range of programs designed for athletes of all ages
              and skill levels.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <img
                src="/assets/program-1.jpg"
                alt="Youth Program"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                  Youth Program
                </h3>
                <p className="text-gray-600 mb-4">
                  Designed for children ages 5-12, focusing on fundamental
                  skills, coordination, and fostering a love for athletics in a
                  fun, supportive environment.
                </p>
                <Link
                  to={`/${tenantId}/programs`}
                  className="text-gray-600 font-semibold hover:text-gray-800"
                >
                  Learn More →
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <img
                src="/assets/program-2.jpg"
                alt="Teen Program"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                  Teen Program
                </h3>
                <p className="text-gray-600 mb-4">
                  For athletes ages 13-17, focusing on advanced techniques,
                  strength development, and competitive preparation in a
                  structured training environment.
                </p>
                <Link
                  to={`/${tenantId}/programs`}
                  className="text-gray-600 font-semibold hover:text-gray-800"
                >
                  Learn More →
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <img
                src="/assets/program-3.jpg"
                alt="Adult Program"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                  Adult Program
                </h3>
                <p className="text-gray-600 mb-4">
                  For adults of all fitness levels, offering personalized
                  training plans, group workouts, and specialized coaching to
                  achieve individual goals.
                </p>
                <Link
                  to={`/${tenantId}/programs`}
                  className="text-gray-600 font-semibold hover:text-gray-800"
                >
                  Learn More →
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to={`/${tenantId}/programs`}
              className="bg-gray-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-gray-700 inline-block"
            >
              View All Programs
            </Link>
          </div>
        </div>
      </section> */}

      {/* Testimonials Section 
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800">
              What Our Members Say
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Hear from our community about their experiences and achievements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-300 mr-4"></div>
                <div>
                  <h4 className="font-semibold text-gray-800">Sarah Johnson</h4>
                  <p className="text-gray-600 text-sm">Member for 2 years</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "The coaches here are exceptional. They've helped my daughter
                build not just athletic skills, but also confidence and
                discipline that translate to all areas of her life."
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-300 mr-4"></div>
                <div>
                  <h4 className="font-semibold text-gray-800">
                    Michael Rodriguez
                  </h4>
                  <p className="text-gray-600 text-sm">Member for 3 years</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "I've trained at several facilities, but none compare to the
                community and quality of coaching here. My performance has
                improved dramatically since joining."
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-300 mr-4"></div>
                <div>
                  <h4 className="font-semibold text-gray-800">Emily Chen</h4>
                  <p className="text-gray-600 text-sm">Member for 1 year</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "As a beginner, I was nervous about joining, but the supportive
                environment made me feel welcome from day one. Now I can't
                imagine my life without this community."
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto px-4 sm:px-0">
            Take the first step toward achieving your athletic goals. Join us
            today and become part of our supportive community.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-4">
            <Link
              to={`/${tenantId}/register`}
              className="bg-[var(--cc-action)] text-[var(--cc-action-text)] px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-[var(--cc-action-hover)] text-center transition-colors duration-200 text-base sm:text-lg"
            >
              {settings?.hero_cta_text || "Sign Up Now"}
            </Link>
            <Link
              to={`/${tenantId}/contact`}
              className="bg-transparent border-2 border-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 text-center transition-all duration-200 text-base sm:text-lg"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
